import { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpcomingEvents } from "@/features/events/api/events";
import { ActivityIndicator, Text, View, Pressable } from "react-native";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Pill } from "@/shared/components/ui/pill";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { RefreshableList } from "@/shared/components/ui/refreshable-list";
import { Link } from "expo-router";
import {
  CalendarPickerModal,
  type DateRange,
} from "@/features/events/components/calendar-picker-modal";
import { EventCard } from "@/features/events/components/event-card";
import { DistanceSelect } from "@/features/events/components/distance-select";
import { FilterModal } from "@/features/events/components/filter-modal";
import {
  SortSelect,
  type SortOption,
} from "@/features/events/components/sort-select";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { haversineDistanceKm } from "@/shared/lib/utils";
import { useUserLocation } from "@/shared/hooks/use-user-location";
import { eventParticipantKeys } from "@/features/events/api/event-participants";
import { sortEvents } from "@/features/events/lib/sort-events";
import type { Event } from "@lezz/shared";
import type { Enums } from "@lezz/supabase";
import { ItemSeparator } from "@/shared/components/ui/item-separator";
import { useTabBarHeight } from "@/shared/hooks/use-tab-bar-height";
import { InviteCallout } from "@/features/callouts";

type TagValue = Enums<"event_tag"> | Enums<"venue_tag"> | Enums<"music_tag">;

/**
 * Module-level so the reference stays stable across renders — an inline arrow
 * would remount the header on every render and inflate callout impressions.
 */
function EventsListCallout() {
  return <InviteCallout placement="events_list" className="mb-3" />;
}

export default function EventsScreen() {
  const queryClient = useQueryClient();
  const {
    data: events,
    isLoading,
    error,
    isSuccess,
    refetch,
  } = useUpcomingEvents();

  const [selectedDistance, setSelectedDistance] = useState(30);
  const {
    location,
    status: locationStatus,
    requestLocation,
  } = useUserLocation();
  const [selectedDateRange, setSelectedDateRange] = useState<
    DateRange | undefined
  >();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<TagValue[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("date");

  const { session } = useAuthContext();
  const tabBarHeight = useTabBarHeight();
  const brand = useCSSVariable("--color-brand") as string;
  const pink = useCSSVariable("--color-pink") as string;
  const brandForeground = useCSSVariable("--color-brand-foreground") as string;

  const hasActiveFilters = selectedTags.length > 0;

  const handleDistanceChange = useCallback(
    (distance: number) => {
      setSelectedDistance(distance);
      if (distance < 30 && locationStatus === "idle") {
        requestLocation();
      }
    },
    [locationStatus, requestLocation]
  );

  const handleSortChange = useCallback(
    (sort: SortOption) => {
      setSortBy(sort);
      if (sort === "distance" && locationStatus === "idle") {
        requestLocation();
      }
    },
    [locationStatus, requestLocation]
  );

  const filteredEvents = useMemo(() => {
    if (!events) return [];

    return events.filter((event) => {
      if (selectedDateRange) {
        if (isNaN(new Date(event.dateAndTime).getTime())) return false;
        const eventTime = new Date(
          new Date(event.dateAndTime).getFullYear(),
          new Date(event.dateAndTime).getMonth(),
          new Date(event.dateAndTime).getDate()
        ).getTime();
        const startTime = new Date(
          selectedDateRange.start.getFullYear(),
          selectedDateRange.start.getMonth(),
          selectedDateRange.start.getDate()
        ).getTime();
        const endTime = new Date(
          selectedDateRange.end.getFullYear(),
          selectedDateRange.end.getMonth(),
          selectedDateRange.end.getDate()
        ).getTime();
        if (eventTime < startTime || eventTime > endTime) return false;
      }

      if (selectedTags.length > 0) {
        const allEventTags = [
          ...((event.eventTags as string[] | null) ?? []),
          ...((event.venueTags as string[] | null) ?? []),
          ...((event.musicTags as string[] | null) ?? []),
        ];
        if (!selectedTags.some((t) => allEventTags.includes(t))) return false;
      }

      if (
        selectedDistance < 30 &&
        location &&
        event.lat != null &&
        event.lng != null
      ) {
        const dist = haversineDistanceKm(
          location.lat,
          location.lng,
          event.lat,
          event.lng
        );
        if (dist > selectedDistance) return false;
      }

      return true;
    });
  }, [events, selectedDateRange, selectedTags, selectedDistance, location]);

  const sortedEvents =
    !filteredEvents || filteredEvents.length === 0
      ? []
      : sortEvents(filteredEvents, sortBy, location);

  const handleRefresh = () =>
    Promise.all([
      refetch(),
      queryClient.invalidateQueries({ queryKey: eventParticipantKeys.all }),
    ]);

  const renderEventCard = useCallback(
    ({ item }: { item: Event }) => <EventCard event={item} />,
    []
  );

  const noUpcomingEvents = isSuccess && events?.length === 0;
  const noFilteredEvents =
    isSuccess && events && events.length > 0 && sortedEvents.length === 0;
  const hasUpcomingEvents = isSuccess && sortedEvents?.length > 0;

  return (
    <ScreenContainer
      className="items-center justify-start p-5"
      minTopPadding={24}
      bottomInset={false}
    >
      {/* Filter bar */}
      <View className="mb-6 w-full flex-row items-center justify-center gap-2">
        {/* Distance selector */}
        <DistanceSelect
          value={selectedDistance}
          onValueChange={handleDistanceChange}
        />

        {/* Calendar circular button */}
        <IconButton
          shape="circle"
          surface={selectedDateRange ? "filled" : "tinted"}
          tone={selectedDateRange ? "brand" : "neutral"}
          accessibilityLabel={
            selectedDateRange ? "Filtro de data ativo" : "Selecionar período"
          }
          accessibilityHint="Toque para selecionar um período. Pressione longamente para limpar."
          onPress={() => setDatePickerOpen(true)}
          onLongPress={() => setSelectedDateRange(undefined)}
        >
          <Ionicons
            name={selectedDateRange ? "today" : "today-outline"}
            size={20}
            color={selectedDateRange ? brandForeground : brand}
          />
        </IconButton>

        {/* Filtros pill button */}
        <Pill
          label="Filtros"
          variant="soft"
          tone="brand"
          selected={hasActiveFilters}
          leftIcon={<Ionicons name="filter-outline" size={18} color={brand} />}
          badge={hasActiveFilters ? selectedTags.length : 0}
          onPress={() => setFilterModalOpen(true)}
          accessibilityLabel="Additional filters"
        />

        {/* Heart circular button */}
        <Link href={`/user/${session?.user.id}/events`} asChild>
          <IconButton
            shape="circle"
            surface="tinted"
            tone="pink"
            accessibilityLabel="Show favorite events"
          >
            <Ionicons name="heart" size={20} color={pink} />
          </IconButton>
        </Link>
      </View>

      {locationStatus === "denied" && selectedDistance < 30 && (
        <Text className="text-muted-foreground mb-2 text-center text-xs">
          Permissão de localização negada. Ative nas configurações do app.
        </Text>
      )}

      {/* Section heading with sort */}
      <View className="mb-3 w-full flex-row items-center justify-between">
        <Text className="text-foreground font-display text-2xl font-semibold">
          Eventos
        </Text>
        <SortSelect value={sortBy} onValueChange={handleSortChange} />
      </View>

      {isLoading && (
        <ActivityIndicator
          size="large"
          className="text-brand mt-10"
          accessibilityLabel="Loading events"
        />
      )}

      {error && (
        <Text
          className="text-destructive mt-5 text-center"
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          Failed to load events
        </Text>
      )}

      {noUpcomingEvents && (
        <Text className="text-muted-foreground mt-5 text-center">
          No upcoming events
        </Text>
      )}

      {noFilteredEvents && (
        <View
          className="mt-5 items-center"
          style={{ paddingBottom: tabBarHeight }}
        >
          <Text className="text-muted-foreground text-center">
            Nenhum evento com os filtros aplicados
          </Text>
          <Pressable
            onPress={() => {
              setSelectedDateRange(undefined);
              setSelectedTags([]);
              setSelectedDistance(30);
            }}
            className="mt-2"
            accessibilityRole="button"
            accessibilityLabel="Limpar filtros"
          >
            <Text className="text-brand font-medium">Limpar filtros</Text>
          </Pressable>
        </View>
      )}

      {hasUpcomingEvents && (
        <View className="mt-2 w-full flex-1">
          <RefreshableList
            data={sortedEvents}
            keyExtractor={(event) => event.eventId.toString()}
            renderItem={renderEventCard}
            contentContainerStyle={{ paddingBottom: tabBarHeight }}
            contentInsetAdjustmentBehavior="automatic"
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={EventsListCallout}
            ItemSeparatorComponent={ItemSeparator}
            onRefresh={handleRefresh}
          />
        </View>
      )}

      <CalendarPickerModal
        open={datePickerOpen}
        onOpenChange={setDatePickerOpen}
        selectedRange={selectedDateRange}
        onConfirm={setSelectedDateRange}
      />

      <FilterModal
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
      />
    </ScreenContainer>
  );
}
